import { useEffect, useState, useCallback } from "react";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { ReportCard } from "../components/ReportCard";
import { ReportDetailModal } from "../components/ReportDetailModal";
import { PLATFORMS, CHEAT_TYPES } from "../lib/constants";
import { Input } from "../components/ui/input";
import { Search as SearchIcon, Loader2, ShieldCheck, ShieldQuestion, UserSearch } from "lucide-react";
import { toast } from "sonner";

const Chip = ({ active, onClick, children, testid }) => (
  <button
    data-testid={testid}
    onClick={onClick}
    className={`whitespace-nowrap rounded-full border px-3 py-1.5 text-xs font-semibold transition-all ${
      active ? "border-[#00F0FF] bg-[#00F0FF]/15 text-[#00F0FF]" : "border-[#1E293F] bg-[#141B2D] text-[#94A3B8] hover:border-[#00F0FF]/40 hover:text-white"
    }`}
  >
    {children}
  </button>
);

export default function Search() {
  const { user, openAuth } = useAuth();
  const [term, setTerm] = useState("");
  const [platform, setPlatform] = useState("");
  const [cheatType, setCheatType] = useState("");
  const [includeSuspects, setIncludeSuspects] = useState(false);
  const [streamersOnly, setStreamersOnly] = useState(false);
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);
  const [selected, setSelected] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setSearched(true);
    try {
      const params = { status: includeSuspects ? "suspect" : "all", sort: "top" };
      if (term) params.search = term;
      if (platform) params.platform = platform;
      if (cheatType) params.cheat_type = cheatType;
      if (streamersOnly) params.streamers = true;
      const calls = [api.get("/reports", { params })];
      if (includeSuspects) {
        // also include the confirmed wall alongside suspects
        const wallParams = { ...params, status: "all" };
        calls.push(api.get("/reports", { params: wallParams }));
      }
      const responses = await Promise.all(calls);
      const merged = responses.flatMap((r) => r.data);
      const seen = new Set();
      const unique = merged.filter((r) => (seen.has(r.id) ? false : seen.add(r.id)));
      unique.sort((a, b) => b.upvotes - a.upvotes);
      setResults(unique);
    } catch {
      toast.error("Search failed.");
    } finally {
      setLoading(false);
    }
  }, [term, platform, cheatType, includeSuspects, streamersOnly]);

  useEffect(() => {
    const t = setTimeout(() => {
      if (term || platform || cheatType || streamersOnly) load();
      else { setResults([]); setSearched(false); }
    }, 300);
    return () => clearTimeout(t);
  }, [term, platform, cheatType, includeSuspects, streamersOnly, load]);

  const handleUpvote = async (report) => {
    if (!user) return openAuth();
    try {
      const { data } = await api.post(`/reports/${report.id}/upvote`);
      setResults((rs) => rs.map((r) => (r.id === data.id ? data : r)));
      if (selected?.id === data.id) setSelected(data);
    } catch { toast.error("Could not register your vote."); }
  };

  const handleDownvote = async (report) => {
    if (!user) return openAuth();
    try {
      const { data } = await api.post(`/reports/${report.id}/downvote`);
      setResults((rs) => rs.map((r) => (r.id === data.id ? data : r)));
      if (selected?.id === data.id) setSelected(data);
    } catch { toast.error("Could not register your vote."); }
  };

  const openDetail = async (report) => {
    setSelected(report);
    try {
      const { data } = await api.get(`/reports/${report.id}`);
      setSelected(data);
    } catch (err) { console.error("Failed to load full case details; showing card data:", err); }
  };

  const clean = searched && !loading && results.length === 0;

  return (
    <div className="mx-auto max-w-5xl px-4 py-10 sm:px-6">
      <div className="animate-fade-up">
        <div className="inline-flex items-center gap-2 rounded-full border border-[#00F0FF]/30 bg-[#00F0FF]/10 px-3 py-1 text-xs font-semibold uppercase tracking-wider text-[#00F0FF]">
          <UserSearch size={13} /> Player Lookup
        </div>
        <h1 className="mt-4 font-heading text-3xl font-black tracking-tight text-white sm:text-4xl">Search a Player</h1>
        <p className="mt-2 text-[#94A3B8]">Check if a gamertag has been flagged as a cheater. Filter by platform and offense.</p>
      </div>

      <div className="animate-fade-up mt-6 rounded-2xl border border-[#1E293F] bg-[#0F1424] p-5" style={{ animationDelay: "80ms" }}>
        <div className="relative">
          <SearchIcon size={18} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-[#64748B]" />
          <Input
            data-testid="player-search-input"
            value={term}
            onChange={(e) => setTerm(e.target.value)}
            placeholder="Enter a gamertag / PSN / Xbox ID…"
            className="h-12 border-[#1E293F] bg-[#141B2D] pl-11 text-base text-white placeholder:text-[#64748B]"
          />
        </div>

        <div className="mt-4">
          <span className="text-xs font-semibold uppercase tracking-wider text-[#64748B]">Platform</span>
          <div className="mt-2 flex flex-wrap items-center gap-2">
            <Chip active={!platform} onClick={() => setPlatform("")} testid="search-platform-all">All</Chip>
            {PLATFORMS.map((p) => (
              <Chip key={p} active={platform === p} onClick={() => setPlatform(platform === p ? "" : p)} testid={`search-platform-${p}`}>{p}</Chip>
            ))}
          </div>
        </div>

        <div className="mt-4">
          <span className="text-xs font-semibold uppercase tracking-wider text-[#64748B]">Offense</span>
          <div className="mt-2 flex flex-wrap items-center gap-2">
            <Chip active={!cheatType} onClick={() => setCheatType("")} testid="search-offense-all">Any</Chip>
            {CHEAT_TYPES.map((c) => (
              <Chip key={c} active={cheatType === c} onClick={() => setCheatType(cheatType === c ? "" : c)} testid={`search-offense-${c}`}>{c}</Chip>
            ))}
          </div>
        </div>

        <label className="mt-4 flex w-fit cursor-pointer items-center gap-2 text-sm text-[#94A3B8]" data-testid="search-include-suspects">
          <input type="checkbox" checked={includeSuspects} onChange={(e) => setIncludeSuspects(e.target.checked)} className="h-4 w-4 accent-[#F97316]" />
          Include unverified community suspects
        </label>

        <label className="mt-2 flex w-fit cursor-pointer items-center gap-2 text-sm text-[#94A3B8]" data-testid="search-streamers-only">
          <input type="checkbox" checked={streamersOnly} onChange={(e) => setStreamersOnly(e.target.checked)} className="h-4 w-4 accent-[#9146FF]" />
          🎥 Streamers only (confirmed cheaters who stream)
        </label>
      </div>

      <div className="mt-8">
        {loading ? (
          <div className="flex justify-center py-16 text-[#64748B]"><Loader2 className="animate-spin" /></div>
        ) : clean ? (
          <div className="rounded-xl border border-dashed border-[#00FF87]/30 bg-[#00FF87]/[0.05] py-16 text-center" data-testid="search-clean">
            <ShieldCheck className="mx-auto mb-3 text-[#00FF87]" size={38} />
            <p className="font-heading text-lg font-bold text-white">No records found</p>
            <p className="mt-1 text-[#94A3B8]">This player isn't currently flagged{term ? ` as "${term}"` : ""}{platform ? ` on ${platform}` : ""}. That doesn't guarantee they're clean — just that no vetted report exists yet.</p>
          </div>
        ) : results.length > 0 ? (
          <>
            <div className="mb-4 flex items-center gap-2 text-sm text-[#94A3B8]" data-testid="search-result-count">
              <ShieldQuestion size={16} className="text-[#FF2A55]" />
              <span className="font-semibold text-white">{results.length}</span> record(s) found
            </div>
            <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3" data-testid="search-results-grid">
              {results.map((r, i) => (
                <ReportCard key={r.id} report={r} index={i} onUpvote={handleUpvote} onDownvote={handleDownvote} onOpen={openDetail} />
              ))}
            </div>
          </>
        ) : (
          <div className="rounded-xl border border-dashed border-[#1E293F] bg-[#0F1424] py-16 text-center text-[#94A3B8]" data-testid="search-idle">
            <UserSearch className="mx-auto mb-3 text-[#334155]" size={36} />
            Start typing a gamertag or pick a filter to search the case files.
          </div>
        )}
      </div>

      <ReportDetailModal report={selected} open={!!selected} onClose={() => setSelected(null)} onUpvote={handleUpvote} onDownvote={handleDownvote} />
    </div>
  );
}
